import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/auth-context';
import BidsAPI from '../../services/api/bids/bids-api';
import Logger from '../../utils/logger';

let divisionsPromise = null;

/** Forget the cached divisions (tests, or after adding a division). */
export const clearBidDivisionsCache = () => {
  divisionsPromise = null;
};

const loadDivisions = (token) => {
  if (!divisionsPromise) {
    divisionsPromise = BidsAPI.getDivisions(token).catch((err) => {
      divisionsPromise = null;
      throw err;
    });
  }
  return divisionsPromise;
};

/**
 * Active divisions, in Estimate Log order.
 * @returns {{ divisions: Array, isLoading: boolean, error: Error|null }}
 */
export function useBidDivisions() {
  const { token } = useContext(AuthContext);
  const [divisions, setDivisions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;

    setIsLoading(true);
    setError(null);
    loadDivisions(token)
      .then((data) => {
        if (!cancelled) setDivisions(data);
      })
      .catch((err) => {
        if (cancelled) return;
        Logger.error('Failed to load bid divisions', err);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return { divisions, isLoading, error };
}

/**
 * A list of bids. Re-fetches when group, divisionId or limit change.
 *
 * @param {object} [options]
 * @param {string} [options.group='all'] open | won | closed | all
 * @param {number} [options.divisionId]
 * @param {number} [options.limit]
 * @param {boolean} [options.enabled=true] false skips fetching (e.g. a closed panel)
 * @returns {{ bids: Array, isLoading: boolean, error: Error|null, reload: Function }}
 */
export function useBidList({
  group = 'all',
  divisionId,
  limit,
  enabled = true,
} = {}) {
  const { token } = useContext(AuthContext);
  const [bids, setBids] = useState([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  // When a fetch is about to start (enabled, filters changed, reload), report
  // loading in this same render, so callers never see an empty "no bids" frame
  // before the effect runs.
  const requestKey = `${enabled}|${group}|${divisionId}|${limit}|${reloadKey}`;
  const [lastRequestKey, setLastRequestKey] = useState(requestKey);
  if (requestKey !== lastRequestKey) {
    setLastRequestKey(requestKey);
    if (enabled && token) setIsLoading(true);
  }

  useEffect(() => {
    if (!enabled || !token) return undefined;
    let cancelled = false;

    setIsLoading(true);
    setError(null);
    BidsAPI.list(token, { group, divisionId, limit })
      .then((data) => {
        if (!cancelled) setBids(data);
      })
      .catch((err) => {
        if (cancelled) return;
        Logger.error('Failed to load bids', err);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, token, group, divisionId, limit, reloadKey]);

  return { bids, isLoading, error, reload };
}

/**
 * One bid by id. A 404 sets notFound (and leaves error null) so the page can
 * show "No bid selected" instead of an error.
 *
 * @param {string|null} bidId - null loads nothing
 * @returns {{ bid: object|null, isLoading: boolean, error: Error|null,
 *   notFound: boolean, reload: Function, setBid: Function }}
 *   setBid replaces the loaded bid, e.g. with the result of a save.
 */
export function useBid(bidId) {
  const { token } = useContext(AuthContext);
  const [bid, setBid] = useState(null);
  const [isLoading, setIsLoading] = useState(!!bidId);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    setBid(null);
    setError(null);
    setNotFound(false);
    if (!bidId || !token) {
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;

    setIsLoading(true);
    BidsAPI.get(bidId, token)
      .then((data) => {
        if (!cancelled) setBid(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          Logger.error('Failed to load bid', err);
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bidId, token, reloadKey]);

  return { bid, isLoading, error, notFound, reload, setBid };
}
